// components/ThemedDatePicker.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const MONTHS_TH = ["ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."];
const WEEK_TH = ["อา","จ","อ","พ","พฤ","ศ","ส"];

function fmt(d){ const y=d.getFullYear(), m=`${d.getMonth()+1}`.padStart(2,"0"), dd=`${d.getDate()}`.padStart(2,"0"); return `${y}-${m}-${dd}`; }
function ymd(d){ return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

export default function ThemedDatePicker({
  value, onChange, min, max, label = "วันเกิด"
}){
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popRef = useRef(null);

  const today = useMemo(()=>new Date(),[]);
  const selected = useMemo(()=> value ? new Date(value) : null, [value]);
  const minDate = min ? new Date(min) : null;
  const maxDate = max ? new Date(max) : null;

  // ปีที่ให้เลือก (กำหนดจาก min/max ถ้ามี)
  const yearStart = minDate ? minDate.getFullYear() : 1900;
  const yearEnd   = maxDate ? maxDate.getFullYear() : today.getFullYear();
  const years = Array.from({length: yearEnd - yearStart + 1}, (_,i)=>yearEnd - i); // มากไปน้อย

  // เดือนที่แสดงตอนเปิด
  const [viewY, setViewY] = useState(selected ? selected.getFullYear() : today.getFullYear());
  const [viewM, setViewM] = useState(selected ? selected.getMonth() : today.getMonth()); // 0-11

  // ปิดเมื่อคลิกนอก
  useEffect(()=>{
    const onDoc = (e)=>{
      if(!open) return;
      if(popRef.current && !popRef.current.contains(e.target) && anchorRef.current && !anchorRef.current.contains(e.target)){
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return ()=>document.removeEventListener("mousedown", onDoc);
  },[open]);

  function daysGrid(y,m){
    const first = new Date(y,m,1);
    const start = first.getDay(); // อา=0
    const daysInMonth = new Date(y, m+1, 0).getDate();
    const cells = [];
    for(let i=0;i<start;i++) cells.push(null);
    for(let d=1; d<=daysInMonth; d++) cells.push(new Date(y,m,d));
    while(cells.length % 7 !== 0) cells.push(null);
    while(cells.length < 42) cells.push(null);
    return cells;
  }
  const grid = daysGrid(viewY, viewM);

  function isDisabled(d){
    if(!d) return true;
    const dd = ymd(d);
    if(minDate && dd < ymd(minDate)) return true;
    if(maxDate && dd > ymd(maxDate)) return true;
    return false;
  }
  function isSameDay(a,b){ return a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }

  // เปลี่ยนเดือน
  function shiftMonth(delta){
    const base = new Date(viewY, viewM + delta, 1);
    // เคารพ min/max โดยปรับให้ยังอยู่ในกรอบ
    if(minDate && ymd(base) < ymd(new Date(minDate.getFullYear(), minDate.getMonth(), 1))){
      setViewY(minDate.getFullYear()); setViewM(minDate.getMonth()); return;
    }
    if(maxDate && ymd(base) > ymd(new Date(maxDate.getFullYear(), maxDate.getMonth(), 1))){
      setViewY(maxDate.getFullYear()); setViewM(maxDate.getMonth()); return;
    }
    setViewY(base.getFullYear()); setViewM(base.getMonth());
  }

  return (
    <div className="relative">
      <label className="block text-sm mb-2 text-left">{label}</label>

      {/* Anchor */}
      <button
        ref={anchorRef}
        type="button"
        onClick={()=>setOpen(v=>!v)}
        className="w-full h-11 rounded-[14px] bg-transparent px-4 text-white text-left
                   outline-none focus:ring-2 focus:ring-[#FFC94A]/60"
        style={{ border: "1.5px solid #FFC94A" }}
      >
        {value
          ? new Date(value).toLocaleDateString("th-TH",{year:"numeric",month:"short",day:"numeric"})
          : "เลือกวันเกิด"}
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute z-30 mt-2 w-[320px] max-w-[92vw] rounded-2xl p-[1.5px] bg-white/80 shadow-[0_16px_36px_rgba(0,0,0,0.55)]"
        >
          <div className="rounded-2xl p-3" style={{background:"linear-gradient(180deg,#0E1732 0%, #0B122A 100%)"}}>
            {/* Header: ปุ่มเดือน + ตัวเลือกเดือน/ปี */}
            <div className="flex items-center justify-between mb-2 gap-2">
              <button
                type="button"
                onClick={()=>shiftMonth(-1)}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/15 flex items-center justify-center"
                aria-label="previous month"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M15 19L8 12L15 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="flex items-center gap-2">
                {/* เดือน */}
                <select
                  value={viewM}
                  onChange={(e)=>setViewM(Number(e.target.value))}
                  className="bg-white/10 text-white text-sm rounded-lg px-2 py-1 outline-none"
                >
                  {MONTHS_TH.map((m,idx)=>(
                    <option key={m} value={idx} className="bg-[#0B122A]">{m}</option>
                  ))}
                </select>
                {/* ปี (พ.ศ. แสดงผล, เก็บเป็น ค.ศ.) */}
                <select
                  value={viewY}
                  onChange={(e)=>setViewY(Number(e.target.value))}
                  className="bg-white/10 text-white text-sm rounded-lg px-2 py-1 outline-none"
                >
                  {years.map(y=>(
                    <option key={y} value={y} className="bg-[#0B122A]">{y+543}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={()=>shiftMonth(1)}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/15 flex items-center justify-center"
                aria-label="next month"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{transform:"rotate(180deg)"}}>
                  <path d="M15 19L8 12L15 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* วันในสัปดาห์ */}
            <div className="grid grid-cols-7 gap-[6px] text-[12px] text-white/70 mb-1">
              {WEEK_TH.map(w => <div key={w} className="text-center">{w}</div>)}
            </div>

            {/* Grid วัน */}
            <div className="grid grid-cols-7 gap-[6px]">
              {grid.map((d,i)=>{
                const disabled = isDisabled(d);
                const selectedDay = d && selected && isSameDay(d, selected);
                const todayDay = d && isSameDay(d, today);

                let cls = "h-9 rounded-xl flex items-center justify-center text-sm transition-colors ";
                if(!d){ cls += "opacity-0"; }
                else if(selectedDay){ cls += "bg-gradient-to-r from-[#FFD86E] to-[#CF7B00] text-[#1B1300] font-extrabold"; }
                else if(disabled){ cls += "text-white/30 border border-white/10"; }
                else { cls += "hover:bg-white/10 text-white/90"; }

                return (
                  <button
                    key={i}
                    type="button"
                    disabled={!d || disabled}
                    onClick={()=>{ onChange?.(fmt(d)); setOpen(false); }}
                    className={cls}
                    style={todayDay && !selectedDay ? { outline:"2px solid rgba(255,217,110,0.5)", outlineOffset:"2px", borderRadius:12 } : undefined}
                  >
                    {d ? d.getDate() : ""}
                  </button>
                );
              })}
            </div>

            {/* วันนี้ / ล้างค่า */}
            {/* <div className="flex justify-between mt-3">
              <button
                type="button"
                onClick={()=>{
                  onChange?.(fmt(today));
                  setViewY(today.getFullYear()); setViewM(today.getMonth());
                  setOpen(false);
                }}
                className="px-3 py-1.5 rounded-full text-xs font-bold text-[#1B1300]"
                style={{background:"linear-gradient(90deg,#FFD86E 0%,#CF7B00 100%)"}}
              >
                วันนี้
              </button>
              <button
                type="button"
                onClick={()=>onChange?.("")}
                className="px-3 py-1.5 rounded-full text-xs text-white/70 hover:bg-white/10"
              >
                ล้างค่า
              </button>
            </div>*/}
          </div>
        </div>
      )}
    </div>
  );
}
